import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { Manager, Reference, Popper } from 'react-popper';
import styles from '@dtsl/css-tooltip/dist/index.module.css';

// eslint-disable-next-line react/display-name
const Tooltip = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    text,
    visible = false,
    placement = 'bottom',
    children,
    onMouseEnter = () => {},
    onMouseLeave = () => {},
    onFocus = () => {},
    onBlur = () => {},
    offset = [0, 8],
    className = '',
    timeout = null,
    tooltipStyles = {}
  } = _ref;
  const [tooltipVisible, setTooltipVisible] = React.useState(false);
  const timeoutId = useRef();
  const lastEventType = useRef(null);
  useImperativeHandle(ref, () => ({
    // used to expose functions to controll Tooltip opening and closing from outside.
    showTooltip: () => {
      showTooltip();
    },
    hideTooltip: () => {
      hideTooltip();
    }
  }));
  const clearTimeoutId = () => {
    clearTimeout(timeoutId?.current);
    timeoutId.current = null;
  };
  const showTooltip = () => {
    if (timeout) {
      if (timeoutId?.current) {
        clearTimeoutId();
      }
      timeoutId.current = setTimeout(() => {
        setTooltipVisible(true);
      }, timeout);
    } else {
      setTooltipVisible(true);
    }
  };
  const hideTooltip = () => {
    if (timeout && timeoutId?.current) {
      clearTimeoutId();
    }
    setTooltipVisible(false);
  };
  const handleMouseEnter = e => {
    e.preventDefault();
    showTooltip();
    onMouseEnter(e);
  };
  const handleMouseLeave = e => {
    e.preventDefault();
    hideTooltip();
    onMouseLeave(e);
  };
  const handleFocus = e => {
    e.preventDefault();
    lastEventType?.current && showTooltip();
    onFocus(e);
  };
  const handleBlur = e => {
    e.preventDefault();
    hideTooltip();
    onBlur(e);
  };
  React.useEffect(() => {
    const handleKeyDown = () => {
      lastEventType.current = 'keypress';
    };
    const handleMouseDown = () => {
      lastEventType.current = null;
    };
    window.addEventListener('keydown', handleKeyDown, {
      capture: true
    });
    window.addEventListener('mousedown', handleMouseDown, {
      capture: true
    });
    return () => {
      window.removeEventListener('keydown', handleKeyDown, {
        capture: true
      });
      window.removeEventListener('mousedown', handleMouseDown, {
        capture: true
      });
    };
  }, [lastEventType?.current]);
  return /*#__PURE__*/React.createElement(Manager, null, /*#__PURE__*/React.createElement(Reference, null, _ref2 => {
    let {
      ref
    } = _ref2;
    return /*#__PURE__*/React.cloneElement(children, {
      ref,
      onMouseEnter: e => handleMouseEnter(e),
      onMouseLeave: e => handleMouseLeave(e),
      onFocus: e => handleFocus(e),
      onBlur: e => handleBlur(e)
    });
  }), visible || tooltipVisible ? /*#__PURE__*/React.createElement(Popper, {
    strategy: "absolute",
    placement: placement,
    eventsEnabled: false,
    modifiers: [{
      name: 'offset',
      options: {
        offset
      }
    }]
  }, _ref3 => {
    let {
      ref,
      style,
      placement
    } = _ref3;
    return /*#__PURE__*/ReactDOM.createPortal(/*#__PURE__*/React.createElement("div", {
      ref: ref,
      id: "tooltip",
      role: "tooltip",
      style: {
        ...style,
        ...tooltipStyles
      },
      className: `${styles['sib-tooltip']} ${className}`,
      "data-placement": placement
    }, text), document.body);
  }) : null);
});
Tooltip.propTypes = {
  text: PropTypes.string,
  visible: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left', 'top-end', 'top-start', 'bottom-end', 'bottom-start', 'left-end', 'left-start', 'right-end', 'right-start']),
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  offset: PropTypes.array,
  timeout: PropTypes.number,
  tooltipStyles: PropTypes.object
};
export default Tooltip;